import { invalidateMatterAndSearchQueries } from "@/lib/data/react-query/search/invalidation";
import {
  ActionConfirmationModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  TextAreaFormField,
} from "@/components/ui";
import { SelectTreatmentAppointmentsFormField } from "@/components/features/matter/medical-treatments/SelectTreatmentAppointmentsFormField";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMedicalTreatmentAppointmentConnection,
  useDeleteMedicalTreatmentAppointmentConnection,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMedicalTreatmentPlan,
  useDeleteMedicalTreatmentPlan,
  useUpdateMedicalTreatmentPlan,
} from "@/lib/data/react-query/matters/medical-treatment-plan";
import { MedicalTreatmentPlanSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  MedicalTreatmentPlan,
  MatterSlideoverInitialTabsType,
} from "@/lib/types";
import { MedicalTreatmentPlanIcon } from "@/lib/utils/iconUtils";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { difference } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditTreatmentPlanSlideoverProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  initialTab?: MatterSlideoverInitialTabsType;
  matterId: string;
  appointmentId?: string;
  planId?: string;
  treatmentId?: string;
  initialData?: Partial<MedicalTreatmentPlan>;
}

const planFormSchema = MedicalTreatmentPlanSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  id: true,
  dateCreated: true,
  dateModified: true,
  matterId: true,
}).extend({
  medicalTreatmentAppointmentId: z.array(z.string()).optional(),
});

const defaultFormValues: z.infer<typeof planFormSchema> = {
  medicalTreatmentAppointmentId: [],
  date: "",
  description: "",
  status: "",
};

export const AddOrEditTreatmentPlanSlideover = ({
  title,
  description,
  open,
  setOpen,
  editing,
  initialTab,
  matterId,
  appointmentId,
  planId,
  treatmentId,
  initialData,
}: AddOrEditTreatmentPlanSlideoverProps) => {
  const queryClient = useQueryClient();
  const { hasActionPermission } = useUser();
  const canCreateAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteAppointmentConnections = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointmentConnection,
    ACTION_PERMISSION_VALUES.delete,
  );
  const createPlan = useCreateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const updatePlan = useUpdateMedicalTreatmentPlan({
    suppressSuccessToast: true,
    suppressQueryInvalidation: true,
  });
  const deletePlan = useDeleteMedicalTreatmentPlan();
  const createMedicalTreatmentAppointmentConnection =
    useCreateMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });
  const deleteMedicalTreatmentAppointmentConnection =
    useDeleteMedicalTreatmentAppointmentConnection({
      suppressQueryInvalidation: true,
    });

  const isFormLoading =
    createPlan.isPending ||
    updatePlan.isPending ||
    createMedicalTreatmentAppointmentConnection.isPending ||
    deleteMedicalTreatmentAppointmentConnection.isPending;

  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch matter data
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );

  const matter = matterData.data;
  const selectedTreatment = treatmentId
    ? matter.medicalTreatments.find((treatment) => treatment.id === treatmentId)
    : undefined;

  // Get the specific plan if editing
  const selectedPlan = planId
    ? matter.medicalTreatmentPlans.find((plan) => plan.id === planId)
    : undefined;
  const planInitialValues = mergeInitialData(selectedPlan, initialData);
  const selectedPlanId = planInitialValues?.id;
  const planConnections = selectedPlan
    ? matter.medicalTreatmentAppointmentConnections.filter(
        (connection) => connection.medicalTreatmentPlanId === selectedPlanId,
      )
    : [];
  const connectedAppointmentIds = planConnections.map(
    (connection) => connection.medicalTreatmentAppointmentId,
  );

  const formValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...(editing && planInitialValues ? planInitialValues : {}),
      medicalTreatmentAppointmentId:
        editing && connectedAppointmentIds.length > 0
          ? connectedAppointmentIds
          : appointmentId
            ? [appointmentId]
            : [],
    }),
    [appointmentId, connectedAppointmentIds, editing, planInitialValues],
  );

  const planForm = useForm<z.infer<typeof planFormSchema>>({
    resolver: zodResolver(planFormSchema),
    values: formValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const isFormActuallyDirty =
    Object.keys(planForm.formState.dirtyFields).length > 0;

  const [closeAfterSave, setCloseAfterSave] = useState(false);

  const onSubmit = async (data: z.infer<typeof planFormSchema>) => {
    const {
      medicalTreatmentAppointmentId: submittedAppointmentIds = [],
      ...rest
    } = data;
    if (!appointmentId && submittedAppointmentIds.length === 0) {
      toast.error("Select at least one treatment date");
      return;
    }
    const existingAppointmentIds = connectedAppointmentIds;
    const appointmentsToAdd = difference(
      submittedAppointmentIds,
      existingAppointmentIds,
    );
    const appointmentsToRemove = difference(
      existingAppointmentIds,
      submittedAppointmentIds,
    );
    if (
      (appointmentsToAdd.length > 0 && !canCreateAppointmentConnections) ||
      (appointmentsToRemove.length > 0 && !canDeleteAppointmentConnections)
    ) {
      return;
    }

    if (editing && selectedPlan && selectedPlanId) {
      try {
        await updatePlan.mutateAsync({
          matterId: matter.id,
          id: selectedPlanId,
          dateModified: selectedPlan.dateModified,
          ...rest,
        });
        await Promise.all([
          ...appointmentsToAdd.map((nextAppointmentId) =>
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentAppointmentId: nextAppointmentId,
              medicalTreatmentPlanId: selectedPlanId,
            }),
          ),
          ...appointmentsToRemove.map((nextAppointmentId) =>
            deleteMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              id:
                matter.medicalTreatmentAppointmentConnections.find(
                  (connection) =>
                    connection.medicalTreatmentAppointmentId ===
                      nextAppointmentId &&
                    connection.medicalTreatmentPlanId === selectedPlanId,
                )?.id ?? "",
            }),
          ),
        ]);
        if (closeAfterSave) setOpen(false);
        toast.success("Treatment plan updated successfully");
        await invalidateMatterAndSearchQueries(queryClient);
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        const res = await createPlan.mutateAsync({
          matterId: matter.id,
          ...rest,
        });
        await Promise.all(
          submittedAppointmentIds.map((nextAppointmentId) =>
            createMedicalTreatmentAppointmentConnection.mutateAsync({
              matterId: matter.id,
              medicalTreatmentAppointmentId: nextAppointmentId,
              medicalTreatmentPlanId: res.data.id,
            }),
          ),
        );
        if (closeAfterSave) setOpen(false);
        toast.success("Treatment plan created successfully");
        await invalidateMatterAndSearchQueries(queryClient);
        // reset form to saved values (if staying open)
        planForm.reset({
          ...defaultFormValues,
          ...res.data,
          medicalTreatmentAppointmentId: submittedAppointmentIds,
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    planForm.register("medicalTreatmentAppointmentId");
  }, [planForm]);

  useEffect(() => {
    if (!open) {
      planForm.reset(defaultFormValues);
    }
  }, [open, planForm]);

  return (
    <>
      <Slideover
        title={title}
        titleIcon={<MedicalTreatmentPlanIcon className="h-4 w-4" />}
        description={description}
        open={open}
        setOpen={setOpen}
        isLoading={isFormLoading}
        editing={editing}
        enabledSections={["notes", "tasks", "events", "references"]}
        matter={matter}
        isDirty={isFormActuallyDirty}
        itemId={selectedPlanId}
        itemKey="medicalTreatmentPlanId"
        subItem={
          selectedPlanId
            ? {
                id: selectedPlanId,
                type: "medical-treatment-plan",
              }
            : undefined
        }
        initialTab={initialTab}
      >
        <Form {...planForm}>
          <form
            className="relative flex grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await planForm.handleSubmit(onSubmit)(e);
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="overflow-y-auto">
              <fieldset
                className={slideoverFieldsetClassName}
                disabled={
                  editing
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matterMedicalTreatmentPlan,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <DateFormField
                  name="date"
                  register={planForm.register("date")}
                  label="Date*"
                />
                <SimpleSelectFormField
                  register={planForm.register("status")}
                  label="Status*"
                  items={["Not Started", "In Progress", "Completed"]}
                  placeholder="Select status"
                />
                <TextAreaFormField
                  className="col-span-full"
                  register={planForm.register("description")}
                  label="Description"
                />
                {!appointmentId && (
                  <fieldset
                    className="contents"
                    disabled={
                      !canCreateAppointmentConnections ||
                      (editing && !canDeleteAppointmentConnections)
                    }
                  >
                    <SelectTreatmentAppointmentsFormField
                      matter={matter}
                      treatment={selectedTreatment}
                      register={planForm.register(
                        "medicalTreatmentAppointmentId",
                      )}
                      label="Treatment date(s)*"
                      placeholder="Select treatment date(s)"
                      className="col-span-full"
                    />
                  </fieldset>
                )}
              </fieldset>
            </div>
            <div className="flex grow" />
            <SheetFooter addSlideoverStyles>
              {selectedPlan &&
                hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterMedicalTreatmentPlan,
                  ACTION_PERMISSION_VALUES.delete,
                ) && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    Delete
                  </Button>
                )}
              <div className="flex grow justify-end gap-x-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  variant="light-primary"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(false);
                  }}
                />
                <FormSubmitButton
                  variant="default"
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  submitFn={() => {
                    setCloseAfterSave(true);
                  }}
                  title="Save and close"
                />
              </div>
            </SheetFooter>
          </form>
        </Form>
      </Slideover>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete treatment plan"
        description="Are you sure you want to delete this plan? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={() => {
          if (selectedPlanId) {
            deletePlan.mutate({
              matterId: matter.id,
              id: selectedPlanId,
            });
            setOpen(false);
          }
        }}
      />
    </>
  );
};
